import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  Polyline,
} from '@react-google-maps/api';
import { PageLayout } from '../components/layout/Navbar';
import { Card, Badge, Spinner, Button } from '../components/ui';
import { orderApi } from '../services/api';
import { GOOGLE_MAPS_API_KEY, isGoogleMapsConfigured } from '../config/googleMaps';
import { useSocketRoute } from '../hooks/useSocketRoute';
import { useSocketEvents } from '../hooks/useSocketEvents';
import GoogleMapsSetupMessage from '../components/GoogleMapsSetupMessage';
import GoogleMapsLoadError from '../components/GoogleMapsLoadError';

const MAX_HISTORY = 80; // max polyline points
const AVG_SPEED_KMH = 30; // assumed city speed for ETA

const mapContainerStyle = { width: '100%', height: '100%', borderRadius: '2.5rem' };

/* ─── Haversine distance (km) ───────────────────────────── */
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* ─── Real-time speed (km/h) from last two positions ────── */
function calcSpeedKmh(prev, curr, intervalMs) {
  if (!prev || intervalMs < 500) return null;
  const dist = haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);
  return (dist / (intervalMs / 3_600_000));
}

/* ─── Animated truck overlay ─────────────────────────────── */
const TruckOverlay = ({ position }) => (
  <OverlayView
    position={position}
    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
  >
    <div
      style={{
        transition: 'transform 0.8s cubic-bezier(0.4,0,0.2,1)',
        willChange: 'transform',
      }}
    >
      {/* Pulse ring */}
      <div style={{
        position: 'absolute', inset: '-14px', borderRadius: '50%',
        background: 'rgba(59,130,246,0.15)',
        animation: 'ping 1.8s cubic-bezier(0,0,0.2,1) infinite',
      }} />
      {/* Truck SVG */}
      <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="22" cy="22" r="22" fill="#2563eb" />
        <circle cx="22" cy="22" r="22" fill="url(#tg)" />
        <defs>
          <radialGradient id="tg" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#1d4ed8" />
          </radialGradient>
        </defs>
        {/* truck body */}
        <rect x="8" y="18" width="18" height="10" rx="2" fill="white" opacity="0.95" />
        {/* cab */}
        <rect x="26" y="20" width="10" height="8" rx="2" fill="white" opacity="0.95" />
        {/* cab window */}
        <rect x="28" y="21.5" width="5" height="3" rx="1" fill="#2563eb" opacity="0.7" />
        {/* wheels */}
        <circle cx="14" cy="29" r="2.5" fill="#1e3a8a" />
        <circle cx="14" cy="29" r="1" fill="white" opacity="0.6" />
        <circle cx="31" cy="29" r="2.5" fill="#1e3a8a" />
        <circle cx="31" cy="29" r="1" fill="white" opacity="0.6" />
        {/* cargo lines */}
        <line x1="12" y1="21" x2="12" y2="27" stroke="#93c5fd" strokeWidth="0.8" />
        <line x1="16" y1="21" x2="16" y2="27" stroke="#93c5fd" strokeWidth="0.8" />
        <line x1="20" y1="21" x2="20" y2="27" stroke="#93c5fd" strokeWidth="0.8" />
      </svg>
    </div>
  </OverlayView>
);

function TrackingMapView({ location, pathHistory, mapOptions, onMapLoad }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-tracking',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  if (loadError) return <GoogleMapsLoadError error={loadError} />;
  if (!isLoaded || !location) {
    return (
      <div className="h-full bg-slate-50 dark:bg-slate-800 rounded-[40px] flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={location}
      zoom={15}
      onLoad={onMapLoad}
      options={mapOptions}
    >
      {pathHistory.length > 1 && (
        <Polyline
          path={pathHistory}
          options={{
            strokeColor: '#3b82f6',
            strokeOpacity: 0.5,
            strokeWeight: 4,
            geodesic: true,
          }}
        />
      )}
      <TruckOverlay position={location} />
    </GoogleMap>
  );
}

const mapStyles = [
  { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#7c93a3' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f0f4f8' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e3eed3' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d2e9f7' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

export default function Tracking() {
  const { t } = useTranslation();
  const [route, setRoute] = useState(null);
  const [order, setOrder] = useState(null);
  const [location, setLocation] = useState(null);
  const [pathHistory, setPathHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eta, setEta] = useState(null);           // minutes (number)
  const [speedKmh, setSpeedKmh] = useState(null);
  const [map, setMap] = useState(null);
  const [activeRouteId, setActiveRouteId] = useState(null);
  const prevLocRef = useRef(null);
  const prevTimeRef = useRef(null);
  const userLatLng = useRef(null); // user's mahalla lat/lng if available

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: false,
    gestureHandling: 'greedy',
    styles: mapStyles,
  }), []);

  const onMapLoad = useCallback((m) => setMap(m), []);

  useSocketRoute(activeRouteId);

  const handleLocationUpdate = useCallback(
    (data) => {
      const now = Date.now();
      const newPos = { lat: parseFloat(data.lat), lng: parseFloat(data.lng) };
      let effectiveSpeed = AVG_SPEED_KMH;

      if (prevLocRef.current && prevTimeRef.current) {
        const speed = calcSpeedKmh(prevLocRef.current, newPos, now - prevTimeRef.current);
        if (speed !== null && speed < 200) {
          setSpeedKmh(Math.round(speed));
          if (speed > 5) effectiveSpeed = speed;
        }
      }
      prevLocRef.current = newPos;
      prevTimeRef.current = now;

      if (userLatLng.current) {
        const distKm = haversineKm(newPos.lat, newPos.lng, userLatLng.current.lat, userLatLng.current.lng);
        setEta(Math.max(1, Math.round((distKm / effectiveSpeed) * 60)));
      }

      setLocation(newPos);
      setPathHistory((prev) => {
        const next = [...prev, newPos];
        return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
      });

      if (map) map.panTo(newPos);
    },
    [map],
  );

  useSocketEvents({
    location_update: handleLocationUpdate,
  });

  /* ─── Init ─────────────────────────────────────────────── */
  const initTracking = useCallback(async () => {
    try {
      const fleetStatus = await orderApi.getFleetStatus();
      if (fleetStatus?.activeRoute) {
        setRoute(fleetStatus.activeRoute);
        setOrder(fleetStatus.userOrder);

        const startLoc = {
          lat: parseFloat(fleetStatus.activeRoute.lastLocationLat) || 41.2995,
          lng: parseFloat(fleetStatus.activeRoute.lastLocationLng) || 69.2401,
        };
        setLocation(startLoc);
        setPathHistory([startLoc]);

        const destLat = parseFloat(fleetStatus.userOrder?.latitude) || null;
        const destLng = parseFloat(fleetStatus.userOrder?.longitude) || null;
        userLatLng.current = destLat && destLng ? { lat: destLat, lng: destLng } : null;

        setActiveRouteId(fleetStatus.activeRoute.id);
      } else {
        setActiveRouteId(null);
      }
    } catch (err) {
      console.error('Tracking init error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initTracking();
  }, [initTracking]);

  /* ─── ETA display ──────────────────────────────────────── */
  const etaLabel = useMemo(() => {
    if (eta === null) return `~${t('common.loading')}`;
    if (eta <= 1) return `< 1 ${t('common.minutes') || 'min'}`;
    return `~${eta} ${t('common.minutes') || 'min'}`;
  }, [eta, t]);

  /* ─── Order step ───────────────────────────────────────── */
  const orderStatusStep = useMemo(() => {
    if (!order) return 1;
    switch (order.status) {
      case 'PENDING': case 'ASSIGNED': return 1;
      case 'DELIVERING': return 2;
      case 'ON_WAY': return 3;
      case 'ARRIVED': case 'COMPLETED': return 4;
      default: return 1;
    }
  }, [order]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0c1510]">
      <Spinner size="lg" />
    </div>
  );

  return (
    <PageLayout>
      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500 italic">
              GPS LIVE
            </span>
          </div>
          <h1 className="text-4xl font-black tracking-tighter italic uppercase dark:text-white mb-2">
            {t('tracking.title')}
          </h1>
          <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest opacity-70">
            {t('tracking.subtitle')}
          </p>
        </motion.header>

        {!route ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="p-24 text-center flex flex-col items-center gap-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl">
              <div className="h-32 w-32 bg-slate-50 dark:bg-slate-800 rounded-[40px] flex items-center justify-center">
                <span className="material-symbols-outlined text-[72px] text-slate-300 fill-1">local_shipping</span>
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black italic uppercase tracking-tight">{t('tracking.noActiveRoute')}</h2>
                <p className="text-slate-400 font-bold italic uppercase text-[10px] tracking-widest">{t('queuePage.emptyState.subtitle')}</p>
              </div>
              <Button onClick={() => window.location.href = '/main'} icon="arrow_forward" size="lg" className="rounded-2xl px-8 h-14 font-black uppercase tracking-widest text-xs">
                {t('common.done')}
              </Button>
            </Card>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* ── Sidebar ── */}
            <div className="lg:col-span-4 space-y-6">
              {/* ETA card */}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <Card className="p-8 border-none bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-[40px] shadow-2xl shadow-blue-500/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-5 mb-6">
                      <div className="h-16 w-16 bg-white/10 rounded-[20px] flex items-center justify-center backdrop-blur-md">
                        <span className="material-symbols-outlined text-4xl fill-1">timer</span>
                      </div>
                      <div>
                        <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.2em] mb-1 italic">{t('queuePage.estimatedTime')}</p>
                        <p className="text-3xl font-black italic uppercase tracking-tight">{etaLabel}</p>
                      </div>
                    </div>

                    {/* Speed chip */}
                    {speedKmh !== null && (
                      <div className="inline-flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2 mb-6">
                        <span className="material-symbols-outlined text-sm">speed</span>
                        <span className="text-[10px] font-black uppercase tracking-widest">{speedKmh} km/h</span>
                      </div>
                    )}

                    <div className="space-y-4 pt-4 border-t border-white/10">
                      <InfoRow label={t('common.driver')} value={route.driver?.name || t('common.user')} light />
                      <InfoRow label={t('tracking.vehicle')} value="ISUZU GAS-402" light />
                      <InfoRow label={t('common.status')} value={
                        <Badge variant="blue" className="bg-white/20 text-white border-none font-black uppercase tracking-widest text-[9px] italic">
                          {t('orderStatus.ON_WAY')}
                        </Badge>
                      } light />
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Steps */}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                <Card className="p-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 italic">{t('queuePage.orderStatus')}</h3>
                  <div className="space-y-8">
                    <Step active={orderStatusStep >= 1} done={orderStatusStep > 1} label={t('queuePage.steps.confirmed.label')} />
                    <Step active={orderStatusStep >= 2} done={orderStatusStep > 2} label={t('queuePage.steps.preparing.label')} />
                    <Step active={orderStatusStep >= 3} done={orderStatusStep > 3} pulse={orderStatusStep === 3} label={t('queuePage.steps.onWay.label')} />
                    <Step active={orderStatusStep >= 4} done={orderStatusStep >= 4} label={t('queuePage.steps.delivered.label')} />
                  </div>
                </Card>
              </motion.div>

              {/* Route history pill */}
              {pathHistory.length > 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                  <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30">
                    <span className="material-symbols-outlined text-sm text-blue-500">route</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                      {pathHistory.length} GPS nuqtasi qayd etildi
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* ── Map ── */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-8 h-[650px]"
            >
              <Card className="h-full p-2 relative shadow-2xl border-none rounded-[44px] bg-white dark:bg-slate-900 overflow-hidden">
                {!isGoogleMapsConfigured() ? (
                  <GoogleMapsSetupMessage
                    extra={location ? (
                      <div className="mt-6 bg-slate-100 dark:bg-slate-700 rounded-2xl px-6 py-4 font-mono text-sm text-slate-600 dark:text-slate-300">
                        📍 {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                      </div>
                    ) : null}
                  />
                ) : (
                  <TrackingMapView
                    location={location}
                    pathHistory={pathHistory}
                    mapOptions={mapOptions}
                    onMapLoad={onMapLoad}
                  />
                )}

                {/* Call driver button */}
                <div className="absolute bottom-8 right-8">
                  <Button variant="primary" icon="call" className="shadow-2xl h-16 px-8 rounded-2xl font-black uppercase tracking-[0.1em] text-xs">
                    {t('tracking.callDriver')}
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

/* ── Sub-components ─────────────────────────────────────── */
const InfoRow = ({ label, value, light }) => (
  <div className={`flex justify-between items-center py-3 border-b ${light ? 'border-white/10' : 'border-slate-50 dark:border-slate-800'} last:border-0`}>
    <span className={`text-[10px] font-black uppercase tracking-widest italic ${light ? 'text-white/60' : 'text-slate-400'}`}>{label}</span>
    <span className={`text-sm font-black italic uppercase ${light ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{value}</span>
  </div>
);

const Step = ({ label, active, done, pulse }) => (
  <div className={`flex items-center gap-5 ${!active && 'opacity-20'}`}>
    <div className={`h-8 w-8 rounded-xl flex items-center justify-center text-white shadow-lg transition-all duration-500 ${
        done ? 'bg-[#1e9f6b] shadow-[#1e9f6b]/30' : pulse ? 'bg-blue-500 animate-pulse shadow-blue-500/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
    }`}>
      {done
        ? <span className="material-symbols-outlined text-[20px]">check</span>
        : <div className="h-2 w-2 rounded-full bg-current" />
      }
    </div>
    <span className={`text-[11px] uppercase tracking-widest ${pulse ? 'font-black text-blue-500 italic' : 'font-bold text-slate-600 dark:text-slate-400'}`}>
      {label}
    </span>
  </div>
);
